import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import panelApi from "@/lib/panelApi";
import { km } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle } from "lucide-react";

export const KM_ALERT_MSG = "Kilometragem indicada bem acima da média do entregador. Deseja confirmar a aprovação mesmo assim?";
const KM_ALERTS_KEY = ["panel-km-alerts"];
const routeKm = (r) => Number(r.corrected_km ?? r.reported_km ?? r.km) || 0;
const dayOf = (r) => String(r.date || r.start_time || "").slice(0, 10);

// Média do km das rotas aprovadas do entregador nos 30 dias anteriores à rota.
export const driverAvg30 = (routes, route) => {
  const ref = new Date(`${dayOf(route)}T12:00:00`);
  const from = new Date(ref); from.setDate(from.getDate() - 30);
  const fromStr = from.toISOString().slice(0, 10);
  const refStr = dayOf(route);
  const list = (routes || []).filter((r) => r.id !== route.id && r.driver_id === route.driver_id
    && r.audit_status === "approved" && routeKm(r) > 0 && dayOf(r) >= fromStr && dayOf(r) <= refStr);
  if (!list.length) return null;
  return list.reduce((a, r) => a + routeKm(r), 0) / list.length;
};

export const useKmAlerts = () => useQuery({
  queryKey: KM_ALERTS_KEY,
  queryFn: async () => (await panelApi.get("/panel/km-alerts")).data?.alerts || {},
});

// gate(route, kmValue, run): se o km for >= 30% acima da média, pede confirmação antes de run(alertInfo).
export const useKmAlertGate = (routes) => {
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const exec = async (run, info, route) => {
    const ok = await run(info);
    if (ok && info) {
      try {
        await panelApi.post("/panel/km-alerts", { route_id: route.id, driver_id: route.driver_id, km: info.km, avg: info.avg });
        qc.invalidateQueries({ queryKey: KM_ALERTS_KEY });
      } catch (e) { /* registro local opcional */ }
    }
    return ok;
  };
  const gate = (route, kmValue, run) => {
    const avg = driverAvg30(routes, route);
    const kmv = Number(kmValue ?? routeKm(route)) || 0;
    if (avg && kmv >= avg * 1.3) { setPending({ route, run, info: { km: kmv, avg } }); return; }
    exec(run, null, route);
  };
  const confirm = async () => {
    setBusy(true);
    try { await exec(pending.run, pending.info, pending.route); } finally { setBusy(false); setPending(null); }
  };
  const dialog = (
    <Dialog open={!!pending} onOpenChange={(o) => !o && !busy && setPending(null)}>
      <DialogContent className="max-w-md border-amber-500/40 bg-slate-900" data-testid="km-alert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-300"><AlertTriangle className="h-5 w-5" /> Kilometragem suspeita</DialogTitle>
          <DialogDescription className="text-base text-slate-200" data-testid="km-alert-message">{KM_ALERT_MSG}</DialogDescription>
        </DialogHeader>
        {pending && (
          <div className="grid grid-cols-3 gap-2 text-center" data-testid="km-alert-numbers">
            <div className="rounded-lg border border-slate-800 bg-slate-950 p-2"><p className="text-[10px] uppercase text-slate-500">KM da rota</p><p className="font-mono text-amber-300">{km(pending.info.km)}</p></div>
            <div className="rounded-lg border border-slate-800 bg-slate-950 p-2"><p className="text-[10px] uppercase text-slate-500">Média 30 dias</p><p className="font-mono text-slate-200">{km(pending.info.avg)}</p></div>
            <div className="rounded-lg border border-slate-800 bg-slate-950 p-2"><p className="text-[10px] uppercase text-slate-500">Acima</p><p className="font-mono text-rose-300">+{Math.round((pending.info.km / pending.info.avg - 1) * 100)}%</p></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="border-slate-700 text-slate-300" disabled={busy} onClick={() => setPending(null)} data-testid="km-alert-cancel">Cancelar</Button>
          <Button className="bg-amber-500 text-slate-950 hover:bg-amber-400" disabled={busy} onClick={confirm} data-testid="km-alert-confirm">Confirmar aprovação</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
  return [gate, dialog];
};
